import { format, getTime, formatDistanceToNow, isSameYear } from "date-fns";

import { ICalendarDate, ICalendarEvent } from "src/types/calendar";
import { ITournamentProfileData } from "src/types/tournament";
import { TIMEZONES } from "src/assets/data/timezones";
import moment from "moment-timezone";
// ----------------------------------------------------------------------

type InputValue = Date | string | number | null | undefined;

export function getTimezoneAdjustedDate(date: Date, timezoneValue: string) {
  const timezone =
    TIMEZONES.find((tz) => tz.value === timezoneValue)?.short ??
    "Europe/Budapest";

  if (!timezone || !date) return new Date(); // Return empty if no timezone or createdAt
  const adjustedDate = new Date(
    moment.tz(date, timezone).format("YYYY-MM-DD HH:mm:ss").replace(/-/g, "/")
  );

  return adjustedDate; // Apply timezone adjustment
}

// export function fDate(date: InputValue, newFormat?: string) {
//   const fm = newFormat || "yyyy MMM dd"; //dd MMM yyyy
//   return date ? format(new Date(date), fm) : "";
// }

export function fDate(date: InputValue, newFormat?: string): string {
  const fm = newFormat || "yyyy MMM dd"; // default format

  if (!date) return "";

  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) {
    return ""; // or "Invalid date"
  }

  return format(parsed, fm);
}

export function fDateMoment(date: string | Date, newFormat?: string): string {
  const fm = newFormat || "YYYY MMM DD"; // Default format
  return date ? moment(date).format(fm) : ""; // Format the date using moment
}

export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || "yyyy MMM dd p";

  return date ? format(new Date(date), fm) : "";
}

export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : "";
}

// export function fToNow(date: InputValue) {
//   return date
//     ? formatDistanceToNow(new Date(date), {
//         addSuffix: true,
//       })
//     : "";
// }
export function fToNow(date: InputValue, locale?: Locale) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
        locale, // <- date-fns locale
      })
    : "";
}

export function displayPeriodStartEnd(periodStart: Date, periodEnd: Date) {
  return `${fDate(periodStart)} - ${fDate(
    periodEnd,
    isSameYear(new Date(periodStart), new Date(periodEnd))
      ? "MMM dd"
      : "yyyy MMM dd"
  )}`;
}

export function combineDateAndTime(date: Date, time: string): Date {
  // Parse the time string
  const [hours, minutes] = time.split(":").map(Number);

  // Create a new Date object based on the input date
  const combinedDate = new Date(date);

  // Set the hours and minutes from the time string
  combinedDate.setHours(hours, minutes, 0, 0); // Set seconds and milliseconds to 0

  return combinedDate;
}

export function formatDateTimeRange(
  startDate?: Date | string | null,
  endDate?: Date | string | null
) {
  const start =
    startDate instanceof Date
      ? startDate
      : startDate
      ? new Date(startDate)
      : null;

  const end =
    endDate instanceof Date ? endDate : endDate ? new Date(endDate) : null;

  if (!start || isNaN(start.getTime())) {
    return "Invalid date";
  }

  const safeEnd = end && !isNaN(end.getTime()) ? end : start;

  const formatDate = (date: Date) => {
    try {
      const isoDate = date.toISOString().split("T")[0];
      return fDate(isoDate, "Y.MM.dd.");
    } catch {
      return "Invalid date";
    }
  };

  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

  const sameDay =
    start.getFullYear() === safeEnd.getFullYear() &&
    start.getMonth() === safeEnd.getMonth() &&
    start.getDate() === safeEnd.getDate();

  if (sameDay) {
    return `${formatDate(start)} ${formatTime(start)}-${formatTime(safeEnd)}`;
  }

  return `${formatDate(start)} ${formatTime(start)} - ${formatDate(
    safeEnd
  )} ${formatTime(safeEnd)}`;
}

// export function formatDateTimeRange(
//   startDate?: Date | string | null,
//   endDate?: Date | string | null
// ) {
//   // Normalize to Date objects
//   const start =
//     startDate instanceof Date
//       ? startDate
//       : startDate
//       ? new Date(startDate)
//       : null;
//   const end =
//     endDate instanceof Date ? endDate : endDate ? new Date(endDate) : null;

//   // Validate start
//   if (!start || isNaN(start.getTime())) {
//     return "Invalid date"; // or ""
//   }

//   // Validate end (fallback: use start if invalid/missing)
//   const safeEnd = end && !isNaN(end.getTime()) ? end : start;

//   // Format date
//   let formattedStartDate = "";
//   try {
//     const isoDate = start.toISOString().split("T")[0]; // safe now
//     formattedStartDate = fDate(isoDate, "Y.MM.dd.");
//   } catch {
//     formattedStartDate = "Invalid date";
//   }

//   // Format times
//   const formattedStartTime = start.toLocaleTimeString([], {
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: false,
//   });
//   const formattedEndTime = safeEnd.toLocaleTimeString([], {
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: false,
//   });

//   return `${formattedStartDate} ${formattedStartTime}-${formattedEndTime}`;
// }

/**
 * Formats a Date object into a time string in "HH:MM" format.
 * @param date - The Date object to format.
 * @returns A time string in "HH:MM" format.
 */
export const getTimeFromDate = (date: Date): string => {
  if (!date) return "";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const getDateTimePart = (datetime: ICalendarDate) =>
  //datetime.toString().split("T")[1].split("+")[0];
  {
    const date = datetime.toString();
    let dateParts = [];
    if (date.includes(" ")) {
      dateParts = date.split(" ");
    } else {
      dateParts = date.split("T");
    }

    if (!dateParts[1].includes("+")) {
      return dateParts[1];
    }
    return datetime.toString().split("T")[1].split("+")[0];
  };

export function getCalendarStartEndTime(events: ICalendarEvent[]): {
  slotMinTime: string;
  slotMaxTime: string;
} {
  // Function to extract the time part from a datetime string

  // Initialize variables with the time part of the first event's dates
  let slotMinTime = getDateTimePart(events[0].start);
  let slotMaxTime = getDateTimePart(events[0].end);

  // Iterate through the events array
  events.forEach((event) => {
    const startTime = getDateTimePart(event.start);
    const endTime = getDateTimePart(event.end);

    if (startTime < slotMinTime) {
      slotMinTime = startTime;
    }
    if (endTime > slotMaxTime) {
      slotMaxTime = endTime;
    }
  });

  slotMinTime = roundTimeToHour(slotMinTime, "down");
  slotMaxTime = roundTimeToHour(slotMaxTime, "up");

  return { slotMinTime, slotMaxTime };
}

/**
 * Rounds the given time to the closest hour based on the direction.
 * @param {string} time - The time string in the format "HH:MM:SS".
 * @param {string} direction - The direction to round ("up" or "down").
 * @returns {string} - The rounded time in the format "HH:00:00".
 */
export function roundTimeToHour(time: string, direction: string) {
  // Split the time string into components
  const [hours, minutes, seconds] = time.split(":").map(Number);

  // Determine the rounded hour based on the direction
  let roundedHour;
  if (direction === "up") {
    roundedHour = minutes > 0 || seconds > 0 ? hours + 1 : hours;
  } else if (direction === "down") {
    roundedHour = hours;
  } else {
    throw new Error('Invalid direction. Use "up" or "down".');
  }

  // Format the rounded hour to "HH:00:00"
  const formattedHour = String(roundedHour).padStart(2, "0");
  return `${formattedHour}:00:00`;
}

/**
 * Calculates the number of hours between two time strings.
 * @param {string} time1 - The start time in the format "HH:MM:SS".
 * @param {string} time2 - The end time in the format "HH:MM:SS".
 * @returns {number} - The number of hours between the two times.
 */
export function calculateHoursBetween(time1: string, time2: string) {
  const [hours1, minutes1] = time1.split(":").map(Number);
  const [hours2, minutes2] = time2.split(":").map(Number);

  const totalSeconds1 = hours1 * 3600 + minutes1 * 60;
  const totalSeconds2 = hours2 * 3600 + minutes2 * 60;

  const differenceInSeconds = totalSeconds2 - totalSeconds1;
  const differenceInHours = differenceInSeconds / 3600;

  return differenceInHours;
}

export function calculateConsumedHours(date1: Date, date2: Date): number {
  // Get the timestamps for each date
  const time1 = date1.getTime();
  const time2 = date2.getTime();

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = time2 - time1;

  // Convert milliseconds to hours
  const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);

  return differenceInHours;
}

/**
 * Extracts the number of minutes from a time string.
 * @param {string} time - The time string in the format "HH:MM:SS".
 * @returns {number} - The number of minutes.
 */
export function extractMinutes(time: string) {
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return minutes;
}

/**
 * Calculates how many times the minutes fit into the given number of hours.
 * @param {number} hours - The number of hours.
 * @param {number} minutes - The minute interval.
 * @returns {number} - The number of times the minutes fit into the hours.
 */
export function calculateMinuteIntervals(hours: number, minutes: number) {
  const totalMinutes = hours * 60;
  return totalMinutes / minutes;
}

/**
 * Determines the appropriate date based on the tournament period and actual date.
 * @param {Date} tournamentStartDate - The start date of the tournament.
 * @param {Date} tournamentEndDate - The end date of the tournament.
 * @param {Date} actualDate - The actual date to check.
 * @returns {Date} - The first day of the tournament or the actual date if within the tournament period.
 */

export function getRelevantDate(
  tournamentStartDate: Date,
  tournamentEndDate: Date,
  actualDate: Date,
  dateFormat: string
) {
  if (
    fDate(actualDate, dateFormat) < fDate(tournamentStartDate, dateFormat) ||
    fDate(actualDate, dateFormat) > fDate(tournamentEndDate, dateFormat)
  ) {
    return tournamentStartDate;
  } else {
    return actualDate;
  }
}

/**
 * Adds hours to a given date.
 * @param date The original date.
 * @param hours The number of hours to add.
 * @returns A new Date object with the added hours.
 */
export const addHoursToDate = (date: Date, hours: number): Date => {
  const newDate = new Date(date.getTime()); // Create a new Date object to avoid mutating the original date
  newDate.setHours(newDate.getHours() + hours); // Add the specified number of hours
  return newDate;
};

// export function addMinutesToDate(date: Date, minutes: number): Date {
//   // Get the current date and time
//   date.setMinutes(date.getMinutes() + minutes); // Add x minutes
//   return date;
// }

export const addMinutesToDate = (date: Date, minutes: number): Date => {
  return new Date(date.getTime() + minutes * 60000); // 60000 ms in a minute
};

/**
 * Generates an array of time intervals between a start and end time.
 * @param startTime - The start time in "HH:mm" format.
 * @param endTime - The end time in "HH:mm" format.
 * @param interval - The interval in "mm" format.
 * @returns An array of time strings in "HH:mm" format.
 */
export const generateTimeIntervals = (
  startTime: string,
  endTime: string,
  interval: string
): string[] => {
  const timeIntervals: string[] = [];

  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);
  const [intervalMinute] = interval.split(":").map(Number);

  if (intervalMinute < 1) {
    return [""];
  }
  let current = new Date();
  current.setHours(startHour, startMinute, 0, 0);

  const end = new Date();
  end.setHours(endHour, endMinute, 0, 0);
  //end.setHours(endHour === 24 ? 23 : endHour, endMinute, 0, 0); // Handle 24:00 edge case

  while (current <= end) {
    const hours = current.getHours().toString().padStart(2, "0");
    const minutes = current.getMinutes().toString().padStart(2, "0");
    timeIntervals.push(`${hours}:${minutes}`);

    current.setMinutes(current.getMinutes() + intervalMinute);
  }

  // Manually add "24:00" if the end time is "24:00"
  if (endHour === 24 && endMinute === 0) {
    // Remove the last "00:00" if it exists
    if (timeIntervals[timeIntervals.length - 1] === "00:00") {
      timeIntervals.pop();
    }
    timeIntervals.push("24:00");
  }

  return timeIntervals;
};

export const addDaysToDate = (
  date: Date,
  days: number,
  direction: "minus" | "plus"
): Date => {
  const newDate = new Date(date.getTime()); // Create a new Date object to avoid mutating the original date

  if (direction === "plus") {
    newDate.setDate(newDate.getDate() + days);
  } else if (direction === "minus") {
    newDate.setDate(newDate.getDate() - days);
  }

  return newDate;
};

/**
 * Returns the start and end dates of the week for a given input date.
 * @param {Date} inputDate - The input date.
 * @returns {Object} - An object with startDate and endDate in 'YYYY-MM-DD' format.
 */
export const getWeekStartAndEndDate = (
  inputDate: Date,
  defaultDateFormat: string
) => {
  // Clone the input date to avoid mutating the original date
  const date = new Date(inputDate);

  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = date.getDay();

  // Calculate the difference to get back to Monday (day 1)
  const startDiff = (dayOfWeek + 6) % 7;

  // Set the start date to the previous Monday
  const startDate = new Date(date);
  startDate.setDate(date.getDate() - startDiff);

  // Set the end date to the following Sunday
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  return {
    startDate: fDate(startDate, defaultDateFormat),
    endDate: fDate(endDate, defaultDateFormat),
  };
};

export function formatMinutesToTime(minutes: string): string {
  const totalMinutes = parseInt(minutes, 10);

  if (isNaN(totalMinutes)) {
    //throw new Error("Invalid input. Please provide a numeric string.");
    return "";
  }

  const hours = Math.floor(totalMinutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (totalMinutes % 60).toString().padStart(2, "0");

  return `${hours}:${mins}:00`;
}

export function isValidDate(dateString: string): boolean {
  // Regular expression to match YYYY-MM-DD format
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (!datePattern.test(dateString)) {
    return false; // Invalid format
  }

  // Parse the date string to a Date object
  const date = new Date(dateString);

  // Extract actual year, month, and day
  const [year, month, day] = dateString.split("-").map(Number);

  return (
    date.getFullYear() === year &&
    date.getMonth() + 1 === month && // getMonth() is zero-based (0 = January)
    date.getDate() === day
  );
}

export const formatSecondsToMinutes = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

export function getYearMonth(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function addMonthsToYearMonth(yearMonth: string, delta: number): string {
  const [year, month] = yearMonth.split("-").map(Number);
  const d = new Date(year, month - 1 + delta, 1);
  return getYearMonth(d);
}
